import * as turf from '@turf/turf';
import {Decoration} from '../../classes/decoration.class';

/**
 * Automatische figuratie-herkenning voor wegmarkering shapefiles uit GISIB / IMGeo
 * (herkenbaar aan de kolommen SOORT_WEGM, SYMBOOL_WE, BREEDTE, LENGTE, OPPERVLAKT en AANTAL).
 *
 * De juiste figuratie hangt af van een combinatie van kolommen en de geometrie (bijv. soort + breedte
 * voor lijnen, symbool + lengte voor punten en de oppervlakte voor vlakken). Daarom voegen we per feature
 * afgeleide kolommen toe die vervolgens via de normale koppelingen van de importer gebruikt worden.
 */
export const GISIB_FIGURATION_PROPERTY = 'VVR_FIGURATIE';
export const GISIB_LENGTH_PROPERTY = 'VVR_LENGTE';
export const GISIB_AMOUNT_PROPERTY = 'VVR_AANTAL';
export const GISIB_SURFACE_PROPERTY = 'VVR_OPPERVLAKTE';

export interface DecorationCandidate {
    group: string;
    name: string;
}

interface Figuration {
    label: string;
    candidates: DecorationCandidate[];
}

/** Omsluitende rechthoek langs één zijde van een vlak: lengte langs de zijde en hoogte erop, in meters. */
interface EdgeDimensions {
    along: number;
    across: number;
}

interface SizeOption {
    size: number;
    label: string;
    candidate: DecorationCandidate;
}

// Lijnen zonder (geldige) breedte krijgen de meest voorkomende breedte voor die soort
const DEFAULT_LINE_WIDTH = 0.10;
const DEFAULT_STOP_LINE_WIDTH = 0.30;
const DEFAULT_CURB_WIDTH = 0.15;
const DEFAULT_BLOCK_WIDTH = 0.50;
const ZEBRA_STRIPE_WIDTH = 0.50;

// GISIB schrijft streeppatronen in meters, de figuraties gebruiken hier en daar een eigen notatie
const STRIPE_PATTERN_NAMES = new Map<string, string>([
    ['0.3-2.7', '30-2,70'],
    ['2.7-0.3', '2,70-30'],
    ['0.3-1.2', '30-1,20'],
    ['0.3-1.5', '30 - 150'],
    ['0.5-0.5', '0.50-0.50']
]);

// Losse driehoeken/blokken worden gekoppeld op de gemeten afmetingen t.o.v. de maat in de figuratienaam
// (bijv. "Haaientand 0,50 * 0,70"), of anders op de oppervlakte per stuk uit decorations.surface.
// Gemeten maten wijken enkele procenten af van de nominale maat.
const SHAPE_TOLERANCE = 0.12;
const SHAPE_SURFACE_TOLERANCE = 0.25;
const DEFAULT_TRIANGLE: DecorationCandidate = {group: 'Driehoeken', name: 'Haaientand 0,50 * 0,50'};

const PROGRESS_CHUNK_SIZE = 250;

// Symboollengtes wijken soms iets af van de standaardmaten, dan kiezen we de dichtstbijzijnde maat
const SYMBOL_LENGTH_TOLERANCE = 0.10;

/**
 * Voegt de afgeleide figuratie- en hoeveelheidskolommen toe aan alle GISIB wegmarkering features.
 * Geeft per figuratie-label de kandidaat figuraties terug (leeg wanneer het geen GISIB bestand is).
 */
export async function enrichGisibRoadMarkings(
    features: any[],
    decorations: Decoration[],
    onProgress?: (done: number, total: number) => void
): Promise<Map<string, DecorationCandidate[]>> {
    const candidatesByLabel = new Map<string, DecorationCandidate[]>();
    if (!features.some(feature => isGisibRoadMarking(feature.properties))) {
        return candidatesByLabel;
    }

    for (let index = 0; index < features.length; index++) {
        // Geef de browser regelmatig de ruimte om de voortgang te tekenen
        if (index % PROGRESS_CHUNK_SIZE === 0) {
            onProgress?.(index, features.length);
            await new Promise(resolve => setTimeout(resolve));
        }
        const feature = features[index];
        const properties = feature.properties ?? {};
        const geometryType: string = feature.geometry?.type ?? '';
        const soort = text(properties.SOORT_WEGM);
        const symbool = text(properties.SYMBOOL_WE);
        const aantal = positiveNumber(properties.AANTAL);

        let figuration: Figuration;
        let length: number = null;
        let amount = 1;
        let surface: number = null;

        if (geometryType.endsWith('LineString')) {
            const width = validLineWidth(properties.BREEDTE);
            const lineLength = positiveNumber(properties.LENGTE) ?? geometryLength(feature.geometry);
            figuration = lineFiguration(soort, width);
            if (isDiscreteLineMarking(soort)) {
                // Lijn waarlangs losse blokken/driehoeken liggen (ongeveer één per meter)
                amount = aantal ?? Math.max(1, Math.round(lineLength ?? 1));
            } else {
                // Een AANTAL bij een streep betekent meerdere evenwijdige strepen
                length = lineLength !== null ? lineLength * (aantal ?? 1) : null;
            }
            surface = length !== null && width !== null ? length * width : null;
        } else if (geometryType.endsWith('Polygon')) {
            surface = positiveNumber(properties.OPPERVLAKT) ?? geometryArea(feature.geometry);
            figuration = polygonFiguration(soort, surface, edgeAlignedDimensions(feature.geometry), decorations);
            length = isZebra(soort) && surface !== null ? surface / ZEBRA_STRIPE_WIDTH : positiveNumber(properties.LENGTE);
        } else {
            const symbolLength = positiveNumber(properties.LENGTE);
            figuration = pointFiguration(soort, symbool, symbolLength);
            amount = aantal ?? 1;
            length = symbolLength;
            surface = positiveNumber(properties.OPPERVLAKT);
        }

        // Zelfde soort met een andere geometrie kan een andere figuratie opleveren, dan het label onderscheiden
        const existing = candidatesByLabel.get(figuration.label);
        if (existing && JSON.stringify(existing) !== JSON.stringify(figuration.candidates)) {
            figuration.label += ` (${geometryName(geometryType)})`;
        }

        // Nieuwe kolommen achteraan toevoegen zodat de automatische veldkoppeling ze boven LENGTE/AANTAL verkiest
        feature.properties = {
            ...properties,
            [GISIB_FIGURATION_PROPERTY]: figuration.label,
            [GISIB_LENGTH_PROPERTY]: round(length),
            [GISIB_AMOUNT_PROPERTY]: amount,
            [GISIB_SURFACE_PROPERTY]: round(surface)
        };
        candidatesByLabel.set(figuration.label, figuration.candidates);
    }
    onProgress?.(features.length, features.length);

    return candidatesByLabel;
}

/** Zoekt de eerste kandidaat die als figuratie bestaat, ongevoelig voor hoofdletters, spaties, quotes en kapotte tekens. */
export function findDecoration(decorations: Decoration[], candidates: DecorationCandidate[]): Decoration | undefined {
    for (const candidate of candidates) {
        const decoration = decorations.find(d =>
            normalizeName(d.decoration_group) === normalizeName(candidate.group) &&
            normalizeName(d.name) === normalizeName(candidate.name)
        );
        if (decoration) {
            return decoration;
        }
    }
    return undefined;
}

function isGisibRoadMarking(properties: any): boolean {
    return !!properties && 'SOORT_WEGM' in properties;
}

function lineFiguration(soort: string, width: number | null): Figuration {
    const s = soort.toLowerCase();

    if (s.includes('combi')) {
        return unmapped(soort);
    }
    if (s.includes('kanalisatie')) {
        const w = width ?? DEFAULT_LINE_WIDTH;
        return {label: widthLabel(soort, w, width), candidates: [{group: 'Blokken', name: `0,50 * ${formatFixed(w)}`}]};
    }
    if (s.includes('blokmarkering')) {
        const w = width ?? DEFAULT_BLOCK_WIDTH;
        return {label: widthLabel(soort, w, width), candidates: [{group: 'Blokken', name: `${formatFixed(w)} * ${formatFixed(w)}`}]};
    }
    if (s.includes('driehoek')) {
        return {label: `${soort} · formaat onbekend (0,50 × 0,50 aangenomen)`, candidates: [DEFAULT_TRIANGLE]};
    }
    if (s.includes('stopstre')) {
        const w = width ?? DEFAULT_STOP_LINE_WIDTH;
        return {label: widthLabel(soort, w, width), candidates: [{group: '(Dwars) strepen', name: `stopstreep ${centimeters(w)}cm`}]};
    }
    if (s.includes('talud')) {
        const w = width ?? DEFAULT_LINE_WIDTH;
        return {label: widthLabel(soort, w, width), candidates: [{group: 'Taludmarkering', name: `Talud ${formatFixed(w)}`}]};
    }

    const pattern = s.includes('doorgetrokken') ? 'vol' : stripePattern(s);
    if (!pattern) {
        return unmapped(soort);
    }

    if (s.includes('band')) {
        // Trottoirband (gele bandmarkering)
        const w = width ?? DEFAULT_CURB_WIDTH;
        const name = pattern === 'vol' ? `Band ${centimeters(w)}cm` : `Band ${pattern} ${centimeters(w)}cm (in lengte)`;
        return {label: widthLabel(soort, w, width), candidates: [{group: 'Symbolen en overige', name}]};
    }

    const w = width ?? DEFAULT_LINE_WIDTH;
    if (pattern === 'vol') {
        return {label: widthLabel(soort, w, width), candidates: [{group: 'Lengtemarkering', name: `streep vol ${centimeters(w)} cm`}]};
    }
    return {
        label: widthLabel(soort, w, width),
        candidates: [
            {group: 'Lengtemarkering', name: `${STRIPE_PATTERN_NAMES.get(pattern) ?? pattern} streep ${centimeters(w)} cm`},
            {group: 'Blokken', name: `${pattern} * ${formatFixed(w)}`} // bijv. "1-3 * 0,30"
        ]
    };
}

function polygonFiguration(soort: string, surface: number | null, dimensions: EdgeDimensions[], decorations: Decoration[]): Figuration {
    const s = soort.toLowerCase();

    if (s.includes('driehoek') || s.includes('blokmarkering')) {
        const triangle = s.includes('driehoek');
        const decoration = nearestDecorationByShape(surface, dimensions, decorations, triangle ? 'Driehoeken' : 'Blokken', triangle);
        if (decoration) {
            return {label: `${soort} · ${decoration.name.trim()}`, candidates: [{group: decoration.decoration_group, name: decoration.name}]};
        }
        if (dimensions.length) {
            // Kleinste omsluitende rechthoek tonen, zodat de gebruiker ziet welke maat het is
            const smallest = dimensions.reduce((a, b) => a.along * a.across <= b.along * b.across ? a : b);
            return unmapped(`${soort} · ± ${formatFixed(smallest.along)} × ${formatFixed(smallest.across)} m`);
        }
        return unmapped(surface !== null ? `${soort} · ± ${formatFixed(surface)} m²` : soort);
    }
    if (isZebra(soort)) {
        return {label: soort, candidates: [{group: 'VOP', name: 'VOP'}]};
    }
    if (s.includes('verdrijf') || s.includes('vlak') || s.includes('puntstuk')) {
        return {label: soort, candidates: [{group: 'Vlak', name: 'vlakken'}]};
    }
    return unmapped(soort);
}

function pointFiguration(soort: string, symbool: string, length: number | null): Figuration {
    const s = symbool.toLowerCase();
    const label = (option: SizeOption | null) => {
        const base = [soort, symbool].filter(Boolean).join(' · ') || 'Onbekend';
        if (!option) {
            return base;
        }
        return length !== null ? `${base} · ${formatNumber(length)} m` : `${base} · lengte onbekend (${option.label} aangenomen)`;
    };
    const sized = (options: SizeOption[], defaultSize: number): Figuration => {
        const option = length !== null
            ? nearestSize(length, options, SYMBOL_LENGTH_TOLERANCE)
            : options.find(o => o.size === defaultSize);
        return option ? {label: label(option), candidates: [option.candidate]} : unmapped(label(null) + (length !== null ? ` · ${formatNumber(length)} m` : ''));
    };
    const sizes = (group: string, values: number[], name: (size: number) => string): SizeOption[] =>
        values.map(size => ({size, label: `${formatNumber(size)} m`, candidate: {group, name: name(size)}}));

    const arrow = arrowName(s);
    if (arrow) {
        return sized(sizes('Pijlen', [2.5, 5, 7.5], size => `${arrow}, ${formatNumber(size)} m'`), 5);
    }
    if (s.includes('fiets')) {
        return sized(sizes('Symbolen en overige', [0.85, 1.35, 1.65, 2, 2.75], size => `fiets ${formatNumber(size)} m'`), 1.35);
    }
    if (/^(30|50|60|70|80)$/.test(s)) {
        return sized(sizes('Cijfers en letters', [1.6, 2.5, 4], size => `""30/50/60/70/80"" ${formatFixed(size)}`), 1.6);
    }
    if (s === 'np') {
        return {label: label(null), candidates: [{group: 'Cijfers en letters', name: '""NP"" 1,00 * 1,00'}]};
    }
    if (['bus', 'lijnbus', 'stop'].includes(s)) {
        return sized(sizes('Cijfers en letters', [1.6, 4], size => `""${s.toUpperCase()}"" ${formatNumber(size)} m'`), 1.6);
    }
    if (s === 'school') {
        return sized([
            ...sizes('Cijfers en letters', [0.6], () => '""SCHOOL"" 60cm\''),
            ...sizes('Cijfers en letters', [1], () => '""SCHOOL"" 1m\''),
            ...sizes('Cijfers en letters', [1.6], () => '""SCHOOL"" 1,60m\'')
        ], 1.6);
    }
    if (s === 'zone') {
        return sized(sizes('Cijfers en letters', [1, 1.6], size => `""ZONE"" ${size === 1 ? '1' : '1,60'} m'`), 1.6);
    }
    if (s.includes('voetganger') || s.includes('poppetje')) {
        return sized(sizes('Symbolen en overige', [0.8, 1, 1.2, 1.6], size => `Poppetje ${formatFixed(size)} m1`), 1.6);
    }
    if (s.includes('driehoek')) {
        // Voorwaarschuwingsdriehoek: 4 m binnen de bebouwde kom, 8 m daarbuiten
        const size = length !== null && length >= 6 ? 8 : 4;
        return {label: label(null) + (length !== null ? ` · ${formatNumber(length)} m` : ''), candidates: [{group: 'Driehoeken', name: `Voorrangsdriehoek ${size} m`}]};
    }
    if (s.includes('miva') || s.includes('rolstoel') || s.includes('invalide')) {
        return {label: label(null), candidates: [{group: 'Symbolen en overige', name: 'Miva symbool 1.35m'}]};
    }
    return unmapped(label(null));
}

function arrowName(symbool: string): string | null {
    if (symbool.includes('rechtuit')) {
        return 'recht';
    }
    if (symbool.includes('dubbele afbuig') || symbool.includes('tweerichting')) {
        return 'tweerichting';
    }
    if (symbool.includes('afbuig')) {
        return 'afslaand';
    }
    if (symbool.includes('combinatie')) {
        return 'combi';
    }
    if (symbool.includes('drierichting')) {
        return 'drierichting';
    }
    if (symbool.includes('verdrijf')) {
        return 'europijl';
    }
    return null; // o.a. rotondepijlen: richting onbekend
}

function geometryName(geometryType: string): string {
    if (geometryType.endsWith('LineString')) {
        return 'lijn';
    }
    if (geometryType.endsWith('Polygon')) {
        return 'vlak';
    }
    return geometryType.endsWith('Point') ? 'punt' : 'geen geometrie';
}

function isDiscreteLineMarking(soort: string): boolean {
    const s = soort.toLowerCase();
    return s.includes('kanalisatie') || s.includes('blokmarkering') || s.includes('driehoek');
}

function isZebra(soort: string): boolean {
    return soort.toLowerCase().includes('zebra');
}

/** "Onderbroken streep 0,3-2,7" => "0.3-2.7", "Kantstreep 1-3" => "1-3" */
function stripePattern(soort: string): string | null {
    const match = soort.match(/(\d+(?:[.,]\d+)?)\s*-\s*(\d+(?:[.,]\d+)?)/);
    if (!match) {
        return null;
    }
    return `${parseFloat(match[1].replace(',', '.'))}-${parseFloat(match[2].replace(',', '.'))}`;
}

/**
 * Figuratie uit de groep die het best past bij het gemeten vlak.
 * - Zijn de afmetingen van het vlak bekend, dan tellen alleen figuraties met een maat in de naam en moeten beide
 *   maten passen. Driehoeken worden als basis × hoogte vergeleken, blokken ongeacht de oriëntatie.
 * - Anders vergelijken we de oppervlakte met decorations.surface.
 * Bij een gelijke score gaat een figuratie met mal voor, daarna de sortering.
 */
function nearestDecorationByShape(
    surface: number | null,
    dimensions: EdgeDimensions[],
    decorations: Decoration[],
    group: string,
    triangle: boolean
): Decoration | null {
    const options = decorations
        .filter(d => normalizeName(d.decoration_group) === normalizeName(group) && !d.specify_length && positiveNumber(d.surface) !== null)
        .map(d => ({decoration: d, difference: shapeDifference(d, surface, dimensions, triangle)}))
        .filter(option => option.difference !== null && option.difference <= SHAPE_TOLERANCE)
        .sort((a, b) =>
            a.difference - b.difference ||
            Number(!!b.decoration.mold) - Number(!!a.decoration.mold) ||
            (a.decoration.order ?? 0) - (b.decoration.order ?? 0));
    return options[0]?.decoration ?? null;
}

/** Relatieve afwijking (0 = exact) tussen het vlak en de figuratie, of null als ze niet te vergelijken zijn. */
function shapeDifference(decoration: Decoration, surface: number | null, dimensions: EdgeDimensions[], triangle: boolean): number | null {
    if (dimensions.length) {
        const size = decorationDimensions(decoration.name);
        // Ook de oppervlakte moet ongeveer kloppen, anders past bijv. een driehoek in de omsluitende maat van een blok
        if (!size || (surface !== null && Math.abs(+decoration.surface - surface) / +decoration.surface > SHAPE_SURFACE_TOLERANCE)) {
            return null;
        }
        const ordered = (a: number, b: number) => triangle ? [a, b] : [Math.max(a, b), Math.min(a, b)];
        const [width, height] = ordered(size[0], size[1]);
        return Math.min(...dimensions.map(dimension => {
            const [along, across] = ordered(dimension.along, dimension.across);
            return Math.max(Math.abs(along - width) / width, Math.abs(across - height) / height);
        }));
    }
    return surface !== null ? Math.abs(+decoration.surface - surface) / +decoration.surface : null;
}

/** "0,50 * 0,70" => [0.5, 0.7] (meters), "Haaientand 20/30" => [0.2, 0.3] (centimeters) */
function decorationDimensions(name: string): [number, number] | null {
    const meters = name.match(/(\d+(?:[.,]\d+)?)\s*\*\s*(\d+(?:[.,]\d+)?)/);
    if (meters) {
        return [parseFloat(meters[1].replace(',', '.')), parseFloat(meters[2].replace(',', '.'))];
    }
    const centimeters = name.match(/(\d{2,3})\s*\/\s*(\d{2,3})/);
    if (centimeters) {
        return [parseInt(centimeters[1], 10) / 100, parseInt(centimeters[2], 10) / 100];
    }
    return null;
}

/**
 * Voor elke zijde van de buitenste ring de omsluitende rechthoek langs die zijde.
 * Bij een rechthoek levert dat de zijden op, bij een gelijkbenige driehoek o.a. basis × hoogte.
 */
function edgeAlignedDimensions(geometry: any): EdgeDimensions[] {
    const ring = ringInMeters(geometry?.type === 'MultiPolygon' ? geometry.coordinates?.[0]?.[0] : geometry?.coordinates?.[0]);
    if (!ring) {
        return [];
    }
    const dimensions: EdgeDimensions[] = [];
    ring.slice(1).forEach((end, i) => {
        const start = ring[i];
        const edgeLength = Math.hypot(end[0] - start[0], end[1] - start[1]);
        if (edgeLength < 0.02) {
            return; // dubbele of bijna dubbele punten
        }
        const [ux, uy] = [(end[0] - start[0]) / edgeLength, (end[1] - start[1]) / edgeLength];
        const along = ring.map(p => p[0] * ux + p[1] * uy);
        const across = ring.map(p => p[1] * ux - p[0] * uy);
        dimensions.push({
            along: Math.max(...along) - Math.min(...along),
            across: Math.max(...across) - Math.min(...across)
        });
    });
    return dimensions;
}

/** RD-coördinaten zijn al meters, WGS84 rekenen we lokaal om (nauwkeurig genoeg voor vlakjes van enkele meters). */
function ringInMeters(ring: number[][]): number[][] | null {
    if (!Array.isArray(ring) || ring.length < 4) {
        return null;
    }
    if (isRdGeometry(ring)) {
        return ring;
    }
    const metersPerDegreeLng = 111320 * Math.cos(ring[0][1] * Math.PI / 180);
    return ring.map(([lng, lat]) => [lng * metersPerDegreeLng, lat * 110540]);
}

function nearestSize(value: number | null, options: SizeOption[], tolerance: number): SizeOption | null {
    if (value === null) {
        return null;
    }
    const nearest = options.reduce((best, option) =>
        Math.abs(option.size - value) < Math.abs(best.size - value) ? option : best);
    return Math.abs(nearest.size - value) / nearest.size <= tolerance ? nearest : null;
}

function widthLabel(soort: string, width: number, originalWidth: number | null): string {
    return originalWidth !== null
        ? `${soort} · ${centimeters(width)} cm`
        : `${soort} · breedte onbekend (${centimeters(width)} cm aangenomen)`;
}

function unmapped(label: string): Figuration {
    return {label: label || 'Onbekend', candidates: []};
}

function validLineWidth(value: any): number | null {
    const width = positiveNumber(value);
    return width !== null && width <= 1 ? width : null;
}

function geometryLength(geometry: any): number | null {
    try {
        const lines: number[][][] = geometry.type === 'MultiLineString' ? geometry.coordinates : [geometry.coordinates];
        if (isRdGeometry(lines)) {
            return lines.reduce((total, line) => total + line.slice(1).reduce((sum, point, i) =>
                sum + Math.hypot(point[0] - line[i][0], point[1] - line[i][1]), 0), 0);
        }
        return turf.length({type: 'Feature', properties: {}, geometry}, {units: 'meters'});
    } catch (e) {
        return null;
    }
}

function geometryArea(geometry: any): number | null {
    try {
        const polygons: number[][][][] = geometry.type === 'MultiPolygon' ? geometry.coordinates : [geometry.coordinates];
        if (isRdGeometry(polygons)) {
            const ringArea = (ring: number[][]) => Math.abs(ring.slice(1).reduce((sum, point, i) =>
                sum + ring[i][0] * point[1] - point[0] * ring[i][1], 0)) / 2;
            return polygons.reduce((total, rings) =>
                total + ringArea(rings[0]) - rings.slice(1).reduce((holes, ring) => holes + ringArea(ring), 0), 0);
        }
        return turf.area({type: 'Feature', properties: {}, geometry});
    } catch (e) {
        return null;
    }
}

/** Shapefiles worden zonder .prj ingelezen; RD-coördinaten (meters) kunnen we direct planair doorrekenen. */
function isRdGeometry(coordinates: any): boolean {
    let point = coordinates;
    while (Array.isArray(point) && Array.isArray(point[0])) {
        point = point[0];
    }
    return Array.isArray(point) && point[0] >= 12000 && point[0] <= 280000 && point[1] >= 300000 && point[1] <= 620000;
}

/** Vergelijkt namen alleen op letters, cijfers en betekenisvolle tekens, zodat bijv. "4 mÂ¹" en "4 m" gelijk zijn. */
function normalizeName(value: string): string {
    return (value ?? '').toLowerCase().replace(/,/g, '.').replace(/[^a-z0-9.*\/<>+-]/g, '');
}

function text(value: any): string {
    return value === null || value === undefined ? '' : String(value).trim();
}

function positiveNumber(value: any): number | null {
    const number = typeof value === 'number' ? value : parseFloat(value);
    return Number.isFinite(number) && number > 0 ? number : null;
}

function round(value: number | null): number | null {
    return value === null ? null : Math.round(value * 100) / 100;
}

function centimeters(meters: number): number {
    return Math.round(meters * 100);
}

/** 0.5 => "0,50" */
function formatFixed(value: number): string {
    return value.toFixed(2).replace('.', ',');
}

/** 1.35 => "1,35", 5 => "5" */
function formatNumber(value: number): string {
    return String(Math.round(value * 100) / 100).replace('.', ',');
}
